<script setup lang="ts">
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import type {
  INewsDataClientDTO,
  INewsItemDataClientDTO,
} from '~/shared/api/client/news/news.dto';
import {
  parseCropRatio,
  resizeCropSlot,
  resizeImage,
  resizeImageSrcsetWidths,
} from '~/shared/lib/resizeImage';
import { CROP_RATIO_CHRONICLE } from '~/shared/lib/crop-ratio.const';
import {
  NEWS_ITEM_WIDE_IMAGE_WIDTH,
  NEWS_ITEM_WIDE_IMAGE_WIDTHS,
} from './lib/const';

type INewsDataClientDTOWithExpert = Omit<INewsDataClientDTO, 'news'> & {
  news: INewsItemDataClientDTO | null;
  backgroundColor: string | null;
};

const props = defineProps<INewsDataClientDTOWithExpert>();

const isRedirect = computed(() => Boolean(props.news?.redirect));

const link = computed(() => {
  if (props.news?.redirect) return props.news.redirect;
  if (props.expert)
    return `/expert/${getMaterialPublicId({ ...props.expert, type: 'expert' })}`;
  if (props.news) return `/${props.news.type}/${props.news.id}`;
  return undefined;
});

const categoryLink = computed(() =>
  props.expert ? '/expert' : `/category/${props.news?.category?.id}`,
);
const categoryName = computed(() =>
  props.expert ? 'Мнения экспертов' : props.news?.category?.name,
);
const imageOne = computed(
  () =>
    props.image_one?.file_url ||
    props.news?.image?.photo.file_url ||
    props.expert?.image?.photo.file_url ||
    undefined,
);
const imageTwo = computed(() => props.image_two?.file_url);
const desktopRatio = parseCropRatio(
  CROP_RATIO_CHRONICLE.image_left_big[0]?.content,
)?.ratio;
const mobileRatio = parseCropRatio(
  CROP_RATIO_CHRONICLE.image_left_big[1]?.content,
)?.ratio;

/* Слот 0 — десктопный кроп; слот 1 — мобильный кроп из редактора. */
const imageOneAttrs = computed(() =>
  resizeCropSlot(imageOne.value, CROP_RATIO_CHRONICLE.image_left_big, 0, {
    width: NEWS_ITEM_WIDE_IMAGE_WIDTH,
  }),
);
const imageOneSrcset = computed(() =>
  resizeImageSrcsetWidths(imageOne.value, NEWS_ITEM_WIDE_IMAGE_WIDTHS, {
    mode: 'fill',
    aspectRatio: desktopRatio,
  }),
);
const imageTwoSrcset = computed(() =>
  resizeImageSrcsetWidths(imageTwo.value, NEWS_ITEM_WIDE_IMAGE_WIDTHS, {
    mode: 'fill',
    aspectRatio: mobileRatio,
  }),
);
const imageTwoAttrs = computed(() =>
  resizeCropSlot(imageTwo.value, CROP_RATIO_CHRONICLE.image_left_big, 1, {
    width: NEWS_ITEM_WIDE_IMAGE_WIDTH,
  }),
);

const partnerCategory = computed(
  () => props.expert?.category ?? props.news?.category ?? null,
);

const isGallery = computed(() => !!props.news?.is_gallery);
const isGalleryPaid = computed(
  () =>
    !!props.news?.is_gallery &&
    !!props.news?.is_paid &&
    !!props.news?.logo?.photo.file_url,
);
</script>

<template>
  <div
    :class="[
      $style.item,
      {
        [$style.item_marked!]:
          props.news?.type === 'new_object' && !props.backgroundColor,
      },
      {
        [$style.item_dark!]:
          props.news?.type === 'new_object' && props.backgroundColor === 'dark',
      },
    ]"
  >
    <PartnerBadge
      :category="partnerCategory"
      :class="$style.item__partnerBadge"
    />

    <NuxtLink
      v-if="imageOne"
      :class="$style.item__imageWrapper"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      <picture :class="$style.item__picture">
        <source
          v-if="imageTwo"
          media="(max-width: 767px)"
          :srcset="imageTwoSrcset || imageTwoAttrs.src"
          sizes="(max-width: 479px) calc(100vw - 32px), calc(100vw - 40px)"
        />
        <img
          :src="imageOneAttrs.src"
          :srcset="imageOneSrcset || undefined"
          sizes="(max-width: 479px) calc(100vw - 32px), (max-width: 767px) calc(100vw - 40px), 320px"
          :alt="props.title"
          :class="$style.item__image"
        />
      </picture>
      <div
        v-if="isGalleryPaid"
        :style="{
          backgroundImage: `url(${resizeImage(props.news?.logo?.photo.file_url, { width: 120 })})`,
        }"
        :class="$style.item__imagePartner"
      />
    </NuxtLink>

    <div :class="$style.item__info">
      <NuxtLink
        v-if="props.category || categoryName"
        :class="$style.item__category"
        :to="categoryLink"
      >
        {{ props.category || categoryName }}
        <Icon v-if="isGallery" name="my-icon:i-camera" size="20" />
      </NuxtLink>
      <NuxtLink
        :class="$style.item__title"
        :to="link"
        :target="isRedirect ? '_blank' : undefined"
      >
        {{ props.title }}
      </NuxtLink>
      <NuxtLink
        v-if="props.subject"
        :class="$style.item__description"
        :to="link"
      >
        <div v-html="props.subject" />
      </NuxtLink>
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-flow: row nowrap;
  padding: 20px;
  gap: 40px;
  position: relative;
  height: 100%;

  &_marked {
    border-left: 4px solid var(--color-red-1);
  }

  &_dark {
    background-color: var(--color-black-1);
  }

  &__image {
    max-width: 320px;
    height: auto;
    display: flex;

    &Wrapper {
      flex-shrink: 0;
      position: relative;
    }

    &Partner {
      position: absolute;
      left: 0;
      top: 0;
      width: 60px;
      height: 60px;
      background-color: var(--color-red-1);
      background-size: 38px;
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  &__picture {
    display: block;
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: 8px;
  }

  &__partnerBadge {
    position: absolute;
    top: var(--space-2-5x);
    right: var(--space-2-5x);
    z-index: 1;
  }

  &__category {
    display: flex;
    align-items: center;
    gap: var(--space-1x);
    @include text_caption2_caps;
    color: var(--color-gray-text);
    text-decoration: none;
  }

  &__title {
    display: block;
    @include text_body1_large_medium;
    color: var(--color-black-1);
    text-decoration: none;
  }

  &__description {
    @include text_body2;
    @include line_clamp(7);
    color: var(--color-black-1);
    text-decoration: none;
  }

  &_dark &__category {
    color: var(--color-gray-text);
  }

  &_dark &__title,
  &_dark &__description {
    color: var(--color-white);
  }
}

@media (max-width: 767px) {
  .item {
    flex-direction: column;
    gap: var(--space-1-5x);

    &__image {
      max-width: 100%;
      width: 100%;
    }

    &__description {
      display: none;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .item {
    padding: 16px;
  }
}
</style>
